import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { writingAPI } from '@/lib/api';
import { PenLine, Sparkles, ArrowUp, ArrowDown, CheckCircle2, ChevronDown, Mic, Square } from 'lucide-react';
import { toast } from 'sonner';
import useMicRecorder from './useMicRecorder';
import WritingFollowup from './WritingFollowup';
import { createTypingRecorder } from './typingRecorder';

const C = { card: '#1A2236', gold: '#D4A853', goldLight: '#F5D799', cream: '#F8F5EE', muted: '#94A3B8', teal: '#38BDF8', green: '#34D399' };

const countWords = (t) => (t.match(/\S+/g) || []).length;
const draftKey = (taskId) => `sv-writing-draft-${taskId}`;
const metaKey = (taskId) => `sv-writing-meta-${taskId}`;
const readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode or full */ } };
const dropLS = (k) => { try { localStorage.removeItem(k); } catch { /* private mode */ } };

const SCORE_LABELS = [['ideas', 'Ideas'], ['organization', 'Order'], ['conventions', 'Sentences & spelling'], ['vocabulary', 'Word choice']];

const Bar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-xs mb-1" style={{ color: C.muted }}><span>{label}</span><span>{Math.round(value)}</span></div>
    <div className="h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
      <div className="h-full rounded-full" style={{ width: `${Math.max(2, Math.min(100, value))}%`, background: value >= 70 ? C.green : C.gold }} />
    </div>
  </div>
);

// The child's own writing practice. Tasks start short and get longer and harder as the child improves.
const WritingStudio = () => {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['writing-current'],
    queryFn: async () => (await writingAPI.current()).data,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const { data: history } = useQuery({
    queryKey: ['writing-history'],
    queryFn: async () => (await writingAPI.history(10)).data.pieces,
  });

  const task = data?.task;
  const progress = data?.progress;
  const monitoring = !!data?.settings?.process_monitoring;
  const dictationOn = !!data?.settings?.dictation;

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [result, setResult] = useState(null);
  const [followupDone, setFollowupDone] = useState(false);
  const [note, setNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // typing timeline: what changed and when, never which keys
  const recorder = useRef(createTypingRecorder());
  const dictIds = useRef([]);
  const saveTimer = useRef(null);
  const startedAt = useRef(null);
  const textRef = useRef('');
  textRef.current = text;

  const persist = useCallback(() => {
    if (!task?.id) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      writeLS(draftKey(task.id), textRef.current);
      writeLS(metaKey(task.id), JSON.stringify({ events: recorder.current.events, dictIds: dictIds.current }));
    }, 800);
  }, [task?.id]);

  // restore an unfinished draft (with its typing timeline) whenever a task appears
  useEffect(() => {
    if (!task?.id) return;
    const draft = readLS(draftKey(task.id)) || '';
    let meta = {};
    try { meta = JSON.parse(readLS(metaKey(task.id)) || '{}'); } catch { meta = {}; }
    setText(draft);
    recorder.current.restore(meta.events, draft.length);
    dictIds.current = Array.isArray(meta.dictIds) ? meta.dictIds : [];
    startedAt.current = null;
    setResult(null);
    setFollowupDone(false);
    setNote('');
  }, [task?.id]);

  const onInput = (e) => {
    if (!startedAt.current) startedAt.current = Date.now();
    if (monitoring) recorder.current.record(e.nativeEvent, e.target.value);
    else recorder.current.setLength(e.target.value.length);
    setText(e.target.value);
    setNote('');
    persist();
  };

  // dictation: speak instead of type. The audio is kept for the parent.
  const mic = useMicRecorder({
    maxSeconds: 90,
    onClip: async (blob, secs) => {
      if (!task) return;
      setDictating(true);
      try {
        const { data: res } = await writingAPI.dictate(task.id, blob, secs);
        const base = textRef.current;
        const add = (base && !/\s$/.test(base) ? ' ' : '') + res.text + ' ';
        const next = base + add;
        if (monitoring) recorder.current.addDictation(next.length);
        else recorder.current.setLength(next.length);
        dictIds.current.push(res.id);
        setText(next);
        if (!startedAt.current) startedAt.current = Date.now();
        persist();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'We could not hear that. Please try again.');
      } finally {
        setDictating(false);
      }
    },
  });

  const words = useMemo(() => countWords(text), [text]);
  const minWords = task?.min_words || 0;
  const targetWords = task?.target_words || 0;
  const enough = words >= minWords;

  const submit = async () => {
    if (!task || busy || !enough) return;
    setBusy(true);
    setNote('');
    try {
      const seconds = startedAt.current ? Math.round((Date.now() - startedAt.current) / 1000) : 0;
      const body = { task_id: task.id, text, paste_count: recorder.current.pasteCount, seconds };
      if (monitoring) {
        body.events = recorder.current.events;
        body.device = window.matchMedia?.('(pointer: coarse)').matches ? 'touch' : 'keyboard';
        body.dictation_ids = dictIds.current;
      }
      const { data: res } = await writingAPI.submit(body);
      if (!res.accepted) {
        setNote(res.message);
        return;
      }
      dropLS(draftKey(task.id));
      dropLS(metaKey(task.id));
      setResult(res);
      qc.invalidateQueries({ queryKey: ['writing-history'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not hand in your writing. It is saved here, so try again.');
    } finally {
      setBusy(false);
    }
  };

  const nextTask = () => {
    setResult(null);
    setText('');
    qc.invalidateQueries({ queryKey: ['writing-current'] });
  };

  const needsFollowup = result?.followup_eligible && !followupDone;

  return (
    <div className="p-4 sm:p-5 rounded-2xl mb-4 sm:mb-6" style={{ background: C.card, border: '1px solid rgba(212,168,83,0.35)' }} data-testid="writing-studio">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(212,168,83,0.15)' }}>
          <PenLine size={20} style={{ color: C.gold }} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-bold" style={{ color: C.cream }}>Writing Studio</h3>
          {progress && (
            <p className="text-xs" style={{ color: C.muted }} data-testid="writing-level">
              Step {progress.level} of {progress.max_level} · {progress.level_name}
            </p>
          )}
        </div>
        {progress && (
          <div className="ml-auto flex items-center gap-1.5" title={`${progress.streak} of ${progress.promote_streak} strong pieces toward the next step`}>
            <span className="text-[11px] mr-1" style={{ color: C.muted }}>Next step</span>
            {Array.from({ length: progress.promote_streak }).map((_, i) => (
              <span key={i} className="w-3 h-3 rounded-full" style={{ background: i < progress.streak ? C.gold : 'rgba(255,255,255,0.12)' }} />
            ))}
          </div>
        )}
      </div>

      {isLoading && <p className="text-sm" style={{ color: C.muted }}>Getting your writing job ready…</p>}
      {isError && (
        <p className="text-sm" style={{ color: C.muted }}>
          Your writing job did not load. <button className="underline" style={{ color: C.gold }} onClick={() => refetch()}>Try again</button>
        </p>
      )}

      {task && !result && (
        <>
          <div className="p-3 sm:p-4 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: C.cream }} data-testid="writing-prompt">{task.prompt}</p>
            {task.words_to_try?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-xs" style={{ color: C.muted }}>Try using:</span>
                {task.words_to_try.map((w) => (
                  <span key={w} className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(56,189,248,0.15)', color: C.teal }}>{w}</span>
                ))}
              </div>
            )}
          </div>

          <textarea value={text} onInput={onInput} onChange={() => {}} onPaste={() => recorder.current.markPaste()}
            rows={Math.min(14, Math.max(5, Math.ceil(targetWords / 18)))}
            placeholder={dictationOn ? 'Type here, or tap the microphone and say it…' : 'Start writing here…'} spellCheck
            className="w-full p-3 rounded-xl text-sm sm:text-base outline-none resize-y"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: C.cream, lineHeight: 1.6 }}
            data-testid="writing-textarea" />

          <div className="flex flex-wrap items-center gap-3 mt-2">
            {dictationOn && mic.supported && (
              <button onClick={() => (mic.recording ? mic.stop() : mic.start())} disabled={dictating}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
                style={{ background: mic.recording ? '#EF4444' : 'rgba(56,189,248,0.15)', color: mic.recording ? '#fff' : C.teal, border: '1px solid rgba(56,189,248,0.4)' }}
                data-testid="writing-mic">
                {mic.recording ? <><Square size={14} /> Stop ({mic.seconds}s)</> : dictating ? 'Writing down your words…' : <><Mic size={16} /> Say it</>}
              </button>
            )}
            <div className="flex-1 min-w-[160px]">
              <div className="h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (words / Math.max(1, targetWords)) * 100)}%`, background: enough ? C.green : C.gold }} />
              </div>
              <p className="text-xs mt-1" style={{ color: enough ? C.green : C.muted }} data-testid="writing-count">
                {words} words · write at least {minWords}, aim for {targetWords}
              </p>
            </div>
            <button onClick={submit} disabled={!enough || busy || mic.recording || dictating}
              className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-40"
              style={{ background: C.gold, color: '#1e1e2e' }} data-testid="writing-submit">
              {busy ? 'Reading your writing…' : 'Hand in my writing'}
            </button>
          </div>
          {mic.error && <p className="text-xs mt-2" style={{ color: '#F87171' }}>{mic.error}</p>}
          {note && <p className="text-sm mt-3 p-3 rounded-xl" style={{ background: 'rgba(212,168,83,0.12)', color: C.goldLight }} data-testid="writing-note">{note}</p>}
        </>
      )}

      {result && (
        <div data-testid="writing-result">
          {!result.graded ? (
            <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', color: C.cream }}>
              <CheckCircle2 size={20} style={{ color: C.green }} className="mb-2" />
              <p className="text-sm">{result.message}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-3">
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0"
                  style={{ background: result.passed ? 'rgba(52,211,153,0.15)' : 'rgba(212,168,83,0.15)', color: result.passed ? C.green : C.gold, border: `2px solid ${result.passed ? C.green : C.gold}` }}>
                  {result.score}
                </div>
                <div>
                  <p className="font-bold" style={{ color: C.cream }}>{result.passed ? 'Strong writing!' : 'Good effort. Keep practicing!'}</p>
                  <p className="text-xs" style={{ color: C.muted }}>{result.words} words</p>
                </div>
              </div>
              {result.moved === 'up' && (
                <div className="flex items-center gap-2 p-3 rounded-xl mb-3 font-bold text-sm" style={{ background: 'rgba(52,211,153,0.15)', color: C.green }} data-testid="writing-moved-up">
                  <ArrowUp size={16} /> You moved up to Step {result.progress.level}: {result.progress.level_name}! Your next job will be longer.
                </div>
              )}
              {result.moved === 'down' && (
                <div className="flex items-center gap-2 p-3 rounded-xl mb-3 text-sm" style={{ background: 'rgba(56,189,248,0.12)', color: C.teal }} data-testid="writing-moved-down">
                  <ArrowDown size={16} /> Let's practice a little more at Step {result.progress.level}. You will get stronger!
                </div>
              )}
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.cream }}>{result.feedback}</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                {SCORE_LABELS.map(([k, label]) => <Bar key={k} label={label} value={result.scores?.[k] || 0} />)}
              </div>
              {result.words_used?.length > 0 && (
                <p className="text-xs mb-2" style={{ color: C.teal }}><Sparkles size={12} className="inline mr-1" />You used: {result.words_used.join(', ')}</p>
              )}
              {result.corrections?.length > 0 && (
                <ul className="text-sm mb-3 space-y-1" style={{ color: C.cream }}>
                  {result.corrections.map((c, i) => (
                    <li key={i}><span style={{ color: C.muted, textDecoration: 'line-through' }}>{c.written}</span> → <span style={{ color: C.green }}>{c.better}</span></li>
                  ))}
                </ul>
              )}
              {result.next_step && <p className="text-sm mb-3 p-3 rounded-xl" style={{ background: 'rgba(212,168,83,0.1)', color: C.cream }}><b>Next time:</b> {result.next_step}</p>}
            </>
          )}
          {result.followup_eligible && <WritingFollowup submissionId={result.submission_id} onDone={() => setFollowupDone(true)} />}
          <button onClick={nextTask} disabled={needsFollowup}
            className="px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-40" style={{ background: C.gold, color: '#1e1e2e' }} data-testid="writing-next">
            Next writing job
          </button>
          {needsFollowup && (
            <button onClick={() => setFollowupDone(true)} className="ml-3 text-xs underline" style={{ color: C.muted }} data-testid="writing-skip-followup">
              Skip the questions
            </button>
          )}
        </div>
      )}

      {history?.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1 text-xs font-medium" style={{ color: C.muted }} data-testid="writing-history-toggle">
            <ChevronDown size={14} style={{ transform: showHistory ? 'rotate(180deg)' : 'none' }} /> My writing ({history.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {history.map((p) => (
                <details key={p.id} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <summary className="text-xs cursor-pointer" style={{ color: C.cream }}>
                    {new Date(p.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · Step {p.level} · {p.words} words
                    {p.status === 'graded' ? ` · ${p.score}` : ' · waiting for feedback'}
                  </summary>
                  <p className="text-xs mt-2" style={{ color: C.muted }}>{p.prompt}</p>
                  <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: C.cream }}>{p.text}</p>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WritingStudio;
